import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { useTranslation } from '../../utils/translations';
import { colors } from '../theme/theme';

import type { ContractApiResponse } from '../../services/contract-services';

interface FullyCoveredContractsPanelProps {
    fullyCoveredContracts: ContractApiResponse[];
    /** True when the payment table above is rendering rows — controls this panel's top border radius. */
    hasPaymentRows: boolean;
    /** Callback when a contract card is clicked. */
    onCardClick?: (contract: ContractApiResponse) => void;
}

/** Warning panel listing contracts whose balance is fully covered by scheduled payments (shown below the payment table). */
export const FullyCoveredContractsPanel: React.FC<FullyCoveredContractsPanelProps> = ({
    fullyCoveredContracts,
    hasPaymentRows,
    onCardClick,
}) => {
    const { t } = useTranslation();
    return (
        <Box
            sx={{
                mt: 4,
                mb: 3,
                borderRadius: hasPaymentRows ? '0 0 10px 10px' : 2,
                borderTop: `2px solid ${colors.warningPreset[50]}`,
                overflow: 'hidden',
                bgcolor: colors.greyPreset[5],
            }}
        >
            {/* Header */}
            <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.5 }}>
                <span className="icon-info-filled" style={{ fontSize: 16, color: colors.warningPreset[50] }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: colors.warningPreset[50] }}>
                    Below contracts have their balance fully covered by scheduled payments
                </Typography>
            </Stack>
            {/* Contract cards grid */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    px: 2,
                    pb: 2,
                }}
            >
                {fullyCoveredContracts.map(contractItem => {
                    const contractId = contractItem.AspireId || `Contract ${contractItem.Id}`;
                    return (
                        <Tooltip key={contractId} title={t('contracts.list.cardTooltip')} placement="top" arrow>
                            <Box
                                onClick={() => onCardClick?.(contractItem)}
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1.5,
                                    flex: '1 1 100px',
                                    maxWidth: 180,
                                    cursor: onCardClick ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover': onCardClick
                                        ? {
                                              boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
                                              transform: 'translateY(-2px)',
                                              bgcolor: colors.greyPreset[10],
                                          }
                                        : {},
                                }}
                            >
                                <Typography variant="h6" fontWeight={700} sx={{ color: colors.greyPreset[80] }}>
                                    {contractId}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};
